import {
  InformationCircleOutlineIc,
  ShieldCheckmark2Ic,
  StarIc,
} from '@dsch/dd-icons';
import { useTheme } from 'styled-components';
import { useTrustedDealerModal } from '../../../contexts/TrustedDealerModalContext';

import * as Styled from '../../../features/filters/FiltersCheckbox/FilterCheckbox.styled';

type FiltersCheckboxProps = {
  label: string;
  value: string;
  count?: string;
  isSelected: boolean;
  onSelectOption: (value: string, isSelected: boolean) => void;
  leadingIcon?: {
    name: string;
    url?: string;
  } | null;
  trailingIcon?: {
    name?: string;
    url?: string;
    text?: string;
  } | null;
};

const FiltersCheckbox = (props: FiltersCheckboxProps) => {
  const {
    label,
    value,
    count,
    isSelected,
    onSelectOption,
    leadingIcon,
    trailingIcon,
  } = props;

  const { setIsTrustedDealerModalOpen } = useTrustedDealerModal();
  const theme = useTheme();

  const handleInfoTipClick = () => {
    setIsTrustedDealerModalOpen(true);
  };

  const iconMap = {
    verified: (
      <Styled.Icon>
        <ShieldCheckmark2Ic
          color={theme.colors.GREEN}
          width={16}
          height={16}
          aria-label="Trusted Dealer Badge"
        />
      </Styled.Icon>
    ),
    infoButton: (
      <Styled.InfoIconContainer
        onClick={handleInfoTipClick}
        aria-label="Learn more about Trusted Dealers"
      >
        <InformationCircleOutlineIc
          color={theme.colors.BLUE}
          width={20}
          height={20}
          aria-label="Information Icon"
        />
      </Styled.InfoIconContainer>
    ),
    info: (
      <Styled.Icon>
        <InformationCircleOutlineIc
          color={theme.colors.BLUE}
          width={20}
          height={20}
          aria-label="Information Icon"
        />
      </Styled.Icon>
    ),
    new: (
      <Styled.NewIcon>
        <Styled.NewText aria-label="New Icon">NEW</Styled.NewText>
      </Styled.NewIcon>
    ),
    star: (
      <Styled.Icon gap="S2">
        <StarIc
          color={theme.colors.ORANGE}
          width={16}
          height={16}
          aria-label="Star Icon"
        />
      </Styled.Icon>
    ),
  } as const;

  const getLeadingLabelIcon = () => {
    if (leadingIcon?.name) {
      return iconMap[leadingIcon.name as keyof typeof iconMap];
    }

    return undefined;
  };

  const getTrailingLabelIcon = () => {
    if (trailingIcon?.text) {
      return iconMap['infoButton'];
    }

    if (trailingIcon?.name) {
      return iconMap[trailingIcon.name as keyof typeof iconMap];
    }

    return undefined;
  };

  const leadingLabelIcon = getLeadingLabelIcon();
  const trailingLabelIcon = getTrailingLabelIcon();

  const combinedLabel = (
    <Styled.Container>
      <Styled.Text>{label}</Styled.Text>
      {count && <Styled.Count>{`(${count})`}</Styled.Count>}
    </Styled.Container>
  );

  return (
    <Styled.Checkbox
      key={`option-${value}`}
      checked={isSelected}
      onChange={({ currentTarget: { checked } }) => {
        onSelectOption(value, checked);
      }}
      id={value}
      name={value}
    >
      <Styled.Content>
        {leadingLabelIcon}
        {combinedLabel}
        {trailingLabelIcon}
      </Styled.Content>
    </Styled.Checkbox>
  );
};

export { FiltersCheckbox };
